import { useTranslation } from "react-i18next";
import type { RunGraphNode } from "~/features/runs/runDerive";
import { cn } from "~/foundation/utils/cn";

export interface BreakpointRowProps {
  nodes: RunGraphNode[];
  active: string[];
  busy: boolean;
  /** Always handed the FULL list; an empty array clears every breakpoint. */
  onChange: (nodeIds: string[]) => void;
}

/**
 * One chip per node in the run's graph. Every toggle posts the WHOLE list to
 * `runs.breakpoints` — the endpoint replaces rather than patches, so sending a delta would
 * clear the others. `[]` is the clear.
 *
 * The set is client-side truth only: `RunProjection.ToDto` does not project `breakpoints`
 * (F-3), so what is drawn here is what this browser last posted and a reload starts empty.
 * That is the server's gap, and faking persistence here would hide it.
 */
export function BreakpointRow({ nodes, active, busy, onChange }: BreakpointRowProps) {
  const { t } = useTranslation();
  const set = new Set(active);

  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-divider bg-bg-secondary px-3 py-2.5">
      <span className="text-small text-text-secondary">
        {t("The run pauses before each selected node.")}
      </span>
      <div className="flex flex-wrap items-center gap-1.5">
        {nodes.map((node) => {
          const on = set.has(node.id);
          return (
            <button
              key={node.id}
              type="button"
              disabled={busy}
              aria-pressed={on}
              onClick={() =>
                onChange(on ? active.filter((id) => id !== node.id) : [...active, node.id])
              }
              className={cn(
                "inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-small",
                on
                  ? "border-accent bg-accent/12 text-accent"
                  : "border-input-border bg-bg text-text-secondary hover:bg-hover-bg",
                busy && "opacity-50",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "inline-block h-2 w-2 rounded-full border",
                  on ? "border-accent bg-accent" : "border-text-tertiary",
                )}
              />
              {node.name}
            </button>
          );
        })}
        {active.length > 0 ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => onChange([])}
            className="inline-flex items-center gap-1 rounded-sm border border-input-border bg-bg px-2 py-1 text-small text-text-secondary hover:bg-hover-bg"
          >
            <span aria-hidden>✕</span>
            {t("Clear all")}
          </button>
        ) : null}
      </div>
    </div>
  );
}
